import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

// Accessible, reusable combobox (WAI-ARIA combobox + listbox pattern).
// Editable text input filters `options` as the user types; focus stays on the
// input and a virtual highlight (aria-activedescendant) moves through the list,
// so screen readers announce each option without DOM focus leaving the input.
export default {
  name: 'FormInputCombobox',
  // All human-readable strings are props so they stay CMS-configurable.
  props: {
    options:        { type: Array,  default: () => [] }, // list of display strings
    fieldId:        { type: String, required: true },    // unique id prefix for listbox/option ids
    ariaLabel:      { type: String, default: 'Søg' },
    placeholder:    { type: String, default: '' },
    buttonLabel:    { type: String, default: 'Søg' },
    noResultsLabel: { type: String, default: 'Ingen resultater' }
  },
  emits: ['select'],
  setup(props, { emit }) {
    const rootRef     = ref(null)
    const query       = ref('')
    const isOpen      = ref(false)
    const activeIndex = ref(-1) // -1 = no option highlighted

    // Case-insensitive substring filter; empty query shows everything.
    const filtered = computed(() => {
      const q = query.value.trim().toLowerCase()
      if (!q) return props.options
      return props.options.filter(o => String(o).toLowerCase().includes(q))
    })

    const listboxId = computed(() => `${props.fieldId}-listbox`)
    const getOptionId = (i) => `${props.fieldId}-option-${i}`

    // No conditional expressions in the template — resolve here.
    const activeDescendantId = computed(() =>
      isOpen.value && activeIndex.value >= 0 ? getOptionId(activeIndex.value) : null
    )
    const hasNoResults = computed(() => isOpen.value && filtered.value.length === 0)
    const isActive = (i) => i === activeIndex.value
    const setActive = (i) => { activeIndex.value = i }

    const open  = () => { isOpen.value = true }
    const close = () => { isOpen.value = false; activeIndex.value = -1 }

    const selectOption = (opt) => {
      emit('select', opt)
      query.value = ''          // reset so the next level starts fresh
      close()
    }
    const selectActive = () => {
      const opt = filtered.value[activeIndex.value]
      if (opt != null) selectOption(opt)
    }

    const onInput = () => { open(); activeIndex.value = -1 }
    const onToggle = () => {
      isOpen.value ? close() : open()
      rootRef.value?.querySelector('input')?.focus()
    }

    const move = (delta) => {
      const len = filtered.value.length
      if (!len) return
      if (!isOpen.value) open()
      activeIndex.value = (activeIndex.value + delta + len) % len
    }

    const onKeydown = (e) => {
      if      (e.key === 'ArrowDown') { e.preventDefault(); move(1) }
      else if (e.key === 'ArrowUp')   { e.preventDefault(); move(-1) }
      else if (e.key === 'Home')      { e.preventDefault(); open(); activeIndex.value = 0 }
      else if (e.key === 'End')       { e.preventDefault(); open(); activeIndex.value = filtered.value.length - 1 }
      else if (e.key === 'Enter')     { if (activeIndex.value >= 0) { e.preventDefault(); selectActive() } }
      else if (e.key === 'Escape')    { e.preventDefault(); close() }
    }

    // Close when focus/click leaves the component.
    const onDocumentClick = (e) => {
      if (rootRef.value && !rootRef.value.contains(e.target)) close()
    }
    onMounted(() => document.addEventListener('click', onDocumentClick))
    onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick))

    return {
      rootRef, query, isOpen, filtered,
      listboxId, getOptionId, activeDescendantId, hasNoResults, isActive, setActive,
      open, close, selectOption, onInput, onToggle, onKeydown
    }
  }
}
